import { useRef, useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import { retryFetch } from "@caredna-marlin/utils/client";

type IsochroneMapProps = {
  latitude: number;
  longitude: number;
  mapboxgl_api_key: string;
};

const IsochroneMap = ({ latitude, longitude, mapboxgl_api_key }: IsochroneMapProps) => {
  mapboxgl.accessToken = mapboxgl_api_key;
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const mapLoaded = useRef(false);
  const [isochroneGeoJSON, setIsochroneGeoJSON] = useState<any>(null);

  // Arrays for contour times, colors, and opacities
  const contourTimes = [5, 10, 15, 20];
  const colors = ["#b6f564", "#ede264", "#eda374", "#f28cb1"];
  const opacities = [0.5, 0.4, 0.3, 0.2];

  // Fetch isochrone data for multiple contours when latitude or longitude changes
  useEffect(() => {
    async function fetchIsochrone() {
      try {
        const profile = "driving";
        const contoursParam = contourTimes.join(",");
        const url = `https://api.mapbox.com/isochrone/v1/mapbox/${profile}/${longitude},${latitude}?contours_minutes=${contoursParam}&polygons=true&generalize=100&access_token=${mapboxgl.accessToken}`;
        const response = await retryFetch(() => fetch(url));
        const data = await response.json();
        setIsochroneGeoJSON(data);
      } catch (error) {
        console.error("Error fetching isochrone:", error);
      }
    }
    fetchIsochrone();
  }, [latitude, longitude]);

  // Initialize the map once
  useEffect(() => {
    if (map.current || !mapContainer.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [longitude, latitude],
      zoom: 10,
    });
    map.current.on("load", () => {
      mapLoaded.current = true;
      // If GeoJSON is already available, add the layers.
      if (isochroneGeoJSON) {
        updateIsochroneLayers();
      }
    });
  }, [latitude, longitude, mapContainer.current, map.current]);

  // Add or update the isochrone layers on the map
  const updateIsochroneLayers = () => {
    if (!map.current || !isochroneGeoJSON) return;

    // If the source exists, update its data; otherwise, add the source and layers.
    if (map.current.getSource("isochrones")) {
      (map.current.getSource("isochrones") as mapboxgl.GeoJSONSource).setData(isochroneGeoJSON);
    } else {
      map.current.addSource("isochrones", {
        type: "geojson",
        data: isochroneGeoJSON,
      });

      // Add layers for each contour.
      // Iterate in descending order so that smaller (inner) contours appear on top.
      for (let i = contourTimes.length - 1; i >= 0; i--) {
        const contour = contourTimes[i];
        const layerId = `isochrone-${contour}`;
        map.current.addLayer({
          id: layerId,
          type: "fill",
          source: "isochrones",
          filter: ["==", ["get", "contour"], contour],
          layout: {},
          paint: {
            "fill-color": colors[i],
            "fill-opacity": opacities[i],
          },
        });
      }
    }
  };

  // Update the isochrone layers whenever the fetched GeoJSON changes.
  useEffect(() => {
    if (!map.current || !isochroneGeoJSON) return;
    if (mapLoaded.current) {
      updateIsochroneLayers();
    } else {
      map.current.on("load", updateIsochroneLayers);
    }
  }, [isochroneGeoJSON]);

  return (
    <div
      ref={mapContainer}
      style={{ width: "100%", height: "500px" }}
    />
  );
};

export default IsochroneMap;
